import { captureError, setUser } from '@smg-real-estate/browser-rum';
import { unref } from 'vue';
import { type NavigationGuardNext, type RouteLocationNormalizedGeneric } from 'vue-router';

import auth0 from '@/auth/auth0Plugin';
import { mapAuth0User } from '@/helpers/authUtils';
import LayoutBlank from '@/layouts/LayoutBlank.vue';
import { viewNamePrefix } from '@/router/routerConstants';
import { shouldRedirectToEmailVerification } from '@/router/routerGuardHelpers';
import { TargetPlatform, getCurrentPlatform } from '@/services/envService';
import {
  EVENT,
  EVENT_AUTHENTICATION_ACTION,
  EVENT_AUTHENTICATION_LABEL,
  LOGIN_LOCATION,
  trackEvent,
} from '@/services/tracking/GtmTrackingService';
import { useUserStore } from '@/stores/user';
import { UserGroup } from '@/types';
import type { Auth0VueClient } from '@auth0/auth0-vue';
import {
  checkFeatureFlags,
  isLegacyIs24GuestExemptFromStorage,
  mergeGuestDraftsIfEligible,
  setUserRedirectedNextStep,
} from './guestRedirectHelper';

export default [
  {
    path: 'invoices',
    name: 'invoices',
    component: () => import('@/views/InvoicesPage/InvoicesPage.vue'),
    meta: {
      authorizedGroups: [UserGroup.CUSTOMER_SERVICE_AGENT, UserGroup.PRIVATE],
      viewName: `${viewNamePrefix} Invoices Page`,
    },
  },
  {
    path: 'login-callback',
    component: LayoutBlank,
    // eslint-disable-next-line max-statements
    beforeEnter: async (
      _targetRoute: RouteLocationNormalizedGeneric,
      _sourceRoute: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      try {
        const userStore = useUserStore();
        const appState = await auth0.handleRedirectCallback();
        const targetPath = appState.appState?.targetPath;

        const authenticatedUser = unref(auth0.isAuthenticated)
          ? mapAuth0User(auth0.user.value)
          : null;

        if (!authenticatedUser) {
          throw new Error('Cannot get authenticated user');
        }

        const claims = (auth0 as Auth0VueClient).idTokenClaims?.value;
        trackEvent({
          event: EVENT.AUTHENTICATION,
          h_event_action_007:
            claims && claims['is_new_user']
              ? EVENT_AUTHENTICATION_ACTION.SIGN_UP
              : EVENT_AUTHENTICATION_ACTION.LOGIN,
          h_event_label_008:
            getCurrentPlatform() === TargetPlatform.IS24
              ? EVENT_AUTHENTICATION_LABEL.IS24
              : EVENT_AUTHENTICATION_LABEL.HG,
          h_client_login_user_id_036: authenticatedUser.userId,
          h_client_login_provider_034: claims?.['connection'],
          h_login_location_049: LOGIN_LOCATION,
          h_login_status_035: 'loggedin',
        });

        userStore.setAuthenticatedUser(authenticatedUser, true);
        setUser({
          id: authenticatedUser.userId,
          groups: authenticatedUser.groups,
        });

        const isLegacyIs24GuestExempt = await isLegacyIs24GuestExemptFromStorage();
        if (!isLegacyIs24GuestExempt) {
          setUserRedirectedNextStep(appState?.appState);
          await mergeGuestDraftsIfEligible(appState?.appState, authenticatedUser);
        }

        const featureFlags = checkFeatureFlags(appState?.appState);
        const url = new URL(targetPath ?? '/de/my-ads', window.location.origin);

        if (featureFlags) {
          url.searchParams.set('feature', featureFlags);
        }

        return next({
          path: url.pathname,
          query: Object.fromEntries(url.searchParams.entries()),
          replace: true,
        });
      } catch (error) {
        captureError(error, { service: 'my-ads-route', action: 'login-callback' });
        // Redirect to landing page if error occurs
        return next({
          path: '/de/insertion/inserat-online-erfassen',
          replace: true,
        });
      }
    },
  },
  {
    path: '',
    name: 'my-ads',
    component: () => {
      return import('@/views/MyAdsPage/MyAdsPage.vue');
    },
    meta: {
      authorizedGroups: [UserGroup.CUSTOMER_SERVICE_AGENT, UserGroup.PRIVATE],
      viewName: `${viewNamePrefix} My Ads Page`,
    },
    beforeEnter: async (
      targetRoute: RouteLocationNormalizedGeneric,
      _sourceRoute: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext,
    ) => {
      const userStore = useUserStore();

      if (
        userStore.authenticatedUser &&
        shouldRedirectToEmailVerification(userStore.authenticatedUser)
      ) {
        // If email is not verified, try renewing the token, since the current one
        // may be cached and the user status may not be up-to-date.
        await auth0.getAccessTokenSilently({ cacheMode: 'off' });
        const authenticatedUser = mapAuth0User(auth0.user.value);
        if (authenticatedUser) {
          userStore.setAuthenticatedUser(authenticatedUser, true);
        }

        // If the email is still not verified, redirect to email verification page
        if (authenticatedUser && !authenticatedUser.emailVerified) {
          const { params, fullPath } = targetRoute;

          const baseUrl = import.meta.env.VUE_APP_BASE_URL;
          const redirectUrl = `${baseUrl}/${params.locale}/verify-email`;
          const callbackUrl = `${window.location.origin}/${fullPath}`;
          window.location.replace(`${redirectUrl}?callbackUrl=${encodeURIComponent(callbackUrl)}`);
          return;
        }
      }

      const guestToken = targetRoute.query.token as string;
      if (userStore.isGuest && guestToken) {
        return next({
          path: `/${targetRoute.params.locale}/my-ads`,
          replace: true,
        });
      }

      return next();
    },
  },
];
